/* eslint-disable react/jsx-filename-extension */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState
} from 'react';
import styled from 'styled-components';
import Select, { components } from 'react-select';
import {
  DropdownIndicator,
  formatGroupLabel,
  getTranslatedText,
  IndicatorSeparator,
  AppContext,
  isAuthorised,
  useIsAuthorisedHook,
  CustomMenu,
  EventEmitter,
  reactSelectInlineError,
  toast,
  CustomModal,
  Loader
} from '@nile/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { GET_VIEWS } from '@nile/rmm/src/snmp/SnmpQuery';
import { RMMClient } from '@nile/graphql/src';
import { useMutation, useQuery } from '@apollo/client';
import { SVGIcon } from '@superops/super-icons';
import { ServiceItemSheetContext } from '@nile/client_management/src/Pages/ContractTemplate/NewServiceItemSheet';
import { DELETE_ASSET_VIEW } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { Modal, Banner, Heading, Button } from '@superops/ui-design-system';

const SelectContext = createContext();

const Wrap = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;
  padding-right: 8px;
  width: 100%;
  .value_selector__control {
    min-width: 300px;
  }
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;

  & > *:first-child {
    margin-left: auto;
    margin-right: 10px;
  }

  span {
    visibility: hidden;
  }

  &:hover span {
    visibility: ${(props) => props.canEdit && 'visible'};
  }
`;

const Menu = (props) => {
  const { onAddNewCondition } = useContext(SelectContext);

  return (
    <CustomMenu
      {...props}
      actionComponent={
        <Button plain icon={<SVGIcon name="Add" />} onClick={onAddNewCondition}>
          <FormattedMessage id="admin.serviceCatalog.assetViews.button.newCondition" />
        </Button>
      }
    />
  );
};

function Option({ children, ...rest }) {
  const { onDelete, onEdit, selectedValue } = useContext(SelectContext);

  const isSelected = selectedValue?.id === rest.data.id;

  return (
    <components.Option {...rest} isSelected={isSelected}>
      <IconWrap canEdit={rest.data.customView}>
        {children}
        <span
          onClick={(e) => {
            e.stopPropagation();
            onEdit(rest.data);
          }}
        >
          <SVGIcon name="Edit" color={isSelected ? '#fff' : '#000'} />
        </span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            onDelete(rest.data).then((isSuccess) => {
              if (isSelected && isSuccess) {
                rest.clearValue();
              }
            });
          }}
        >
          <SVGIcon name="Delete" color={isSelected ? '#fff' : '#000'} />
        </span>
      </IconWrap>
    </components.Option>
  );
}

export function ItemQuantitySubType({
  isDisabled,
  onChange,
  selectedValue,
  label
}) {
  const intl = useIntl();

  const serviceItemSheetCtx = useContext(ServiceItemSheetContext);

  const hasAssetViewAccess = useIsAuthorisedHook('assetViews.view');

  const [assetViews, setAssetViews] = useState();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [
    showDeleteExistingConditionModal,
    toggleDeleteExistingConditionModal
  ] = useReducer((s) => !s, false);

  const { loading: assetViewsLoading, refetch: refetchAssetViews } = useQuery(
    GET_VIEWS,
    {
      client: RMMClient,
      variables: {
        modules: ['CONTRACT_ASSET_VIEW']
      },
      fetchPolicy: 'network-only',
      skip: !hasAssetViewAccess,
      onCompleted({ getViews }) {
        if (getViews) {
          setAssetViews(getViews[0]?.views);
        }
      }
    }
  );

  const [deleteAssetView] = useMutation(DELETE_ASSET_VIEW, {
    client: RMMClient,
    onError(error) {
      toggleDeleteExistingConditionModal();
    }
  });

  useEffect(() => {
    EventEmitter.subscribe('refetchGetViews', (data) => {
      refetchAssetViews().then((resp) => {
        if (resp?.data?.getViews) {
          setAssetViews(resp.data.getViews[0]?.views);
        }
      });
    });
    return () => EventEmitter.unsubscribe('refetchGetViews');
  }, [refetchAssetViews]);

  const selectOptions: Array<{
    key: 'default' | 'customCondition';
    label: string;
    options: any;
  }> = useMemo(() => {
    if (!assetViews) return [];
    const customViews = [];
    const defaultViews = [];
    assetViews.forEach((view) => {
      (view.customView ? customViews : defaultViews).push({
        ...view,
        label: view.name
      });
    });

    return [
      {
        key: 'default',
        label: getTranslatedText(
          intl,
          'admin.serviceCatalog.assetViews.default'
        ),
        options: defaultViews
      },
      {
        key: 'customCondition',
        label: getTranslatedText(
          intl,
          'admin.serviceCatalog.assetViews.customCondition'
        ),
        options: customViews
      }
    ];
  }, [assetViews]);

  const onDelete = ({ id }) => {
    setIsMenuOpen(false);
    return deleteAssetView({
      variables: {
        viewID: id,
        module: 'CONTRACT_ASSET_VIEW'
      }
    }).then((resp) => {
      if (resp?.data?.deleteAssetView) {
        EventEmitter.dispatch('refetchGetViews', { operation: 'delete' });
        toast('success', 'toast.admin.serviceCatalog.assetView.delete');
        return true;
      }
      return false;
    });
  };

  const onEdit = (data) => {
    setIsMenuOpen(false);
    serviceItemSheetCtx.setSelectedEditAssetViewData(data);
  };

  const onAddNewCondition = () => {
    serviceItemSheetCtx.setShowAssetViewPage(true);
  };

  const reactSelectValue = useMemo(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const { options } of selectOptions) {
      if (options.length) {
        const value = options.find((view) => view.id === selectedValue?.id);
        if (value) {
          return value;
        }
      }
    }
  }, [selectOptions, selectedValue?.id]);

  if (assetViewsLoading) {
    return (
      <div className="ml-20 h60px">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <ExistingConditionInfoModal
        isOpen={showDeleteExistingConditionModal}
        onClose={toggleDeleteExistingConditionModal}
      />
      {label}
      <div className="flex full-width center g10">
        <Wrap className="item-quantity">
          <SelectContext.Provider
            value={{
              onDelete,
              onEdit,
              onAddNewCondition,
              selectedValue: reactSelectValue
            }}
          >
            <Select
              isClearable
              menuPortalTarget={document.body}
              value={reactSelectValue}
              onChange={onChange}
              options={selectOptions}
              components={{
                IndicatorSeparator,
                DropdownIndicator,
                Option,
                Menu
              }}
              classNamePrefix="value_selector"
              formatGroupLabel={formatGroupLabel}
              menuPlacement="auto"
              placeholder={getTranslatedText(
                intl,
                'admin.serviceCatalog.placeholder.chooseCondition'
              )}
              closeMenuOnScroll={(e) => {
                return (
                  e.target &&
                  e.target.classList &&
                  !(e.target.classList.value.indexOf('menu-list') > -1)
                );
              }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              isDisabled={isDisabled}
              menuIsOpen={isMenuOpen}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuClose={() => setIsMenuOpen(false)}
            />
          </SelectContext.Provider>
        </Wrap>
      </div>
      {serviceItemSheetCtx.hasAssetViewConditionError &&
        reactSelectInlineError(
          <FormattedMessage id="admin.serviceCatalog.assetView.condition.error" />
        )}
    </>
  );
}

function ExistingConditionInfoModal({ isOpen, onClose }) {
  return (
    <CustomModal open={isOpen} onClose={onClose}>
      <div className="white-bg br20 p20">
        <div className="fx g20 mb-15 ">
          <SVGIcon name="WarningIcon" color="#379FFF" size="xl" />
          <Heading element="h1" weight="Bold">
            <FormattedMessage id="admin.serviceCatalog.assetView.deleteExistingAsset.modal" />
          </Heading>
          <Button icon={<SVGIcon name="Close" />} onClick={onClose} />
        </div>
        <div className="modal-body">
          <Banner status="warning">
            <FormattedMessage id="admin.serviceCatalog.assetView.deleteExistingAsset.modal.desc" />
          </Banner>
        </div>
      </div>
    </CustomModal>
  );
}

export default ItemQuantitySubType;
